import React, { useState, useEffect, useRef } from 'react';
import { WindowData, DesktopIcon } from '../types';

interface TaskbarProps {
  windows: WindowData[];
  icons: DesktopIcon[];
  onWindowSelect: (id: string) => void;
  onStartMenuClick: (icon: DesktopIcon) => void;
  activeWindowId: string | null;
}

const Taskbar: React.FC<TaskbarProps> = ({
  windows,
  icons,
  onWindowSelect,
  onStartMenuClick,
  activeWindowId,
}) => {
  const [showStartMenu, setShowStartMenu] = useState(false);
  const [time, setTime] = useState(new Date());
  const startMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        startMenuRef.current &&
        !startMenuRef.current.contains(e.target as Node) &&
        !(e.target as HTMLElement).closest('.start-button')
      ) {
        setShowStartMenu(false);
      }
    };

    if (showStartMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStartMenu]);

  const formatTime = (date: Date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12 || 12;
    const minuteStr = minutes < 10 ? `0${minutes}` : minutes;
    return `${hours}:${minuteStr} ${ampm}`;
  };

  // Show all windows in taskbar
  const visibleWindows = windows;

  const handleWindowClick = (windowId: string, isMinimized: boolean) => {
    // Always restore/focus the window when clicked in taskbar
    onWindowSelect(windowId);
  };

  return (
    <>
      {showStartMenu && (
        <div ref={startMenuRef} className="start-menu">
          <div className="start-menu-items">
            {icons.map((icon) => (
              <div
                key={icon.id}
                className="start-menu-item"
                onClick={() => {
                  onStartMenuClick(icon);
                  setShowStartMenu(false);
                }}
              >
                <span style={{ fontSize: '16px' }}>{icon.icon}</span>
                <span>{icon.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="taskbar">
        <button
          className="start-button"
          onClick={() => setShowStartMenu(!showStartMenu)}
        >
          <span style={{ fontSize: '16px' }}>⊞</span>
          <span>Start</span>
        </button>

        <div className="taskbar-divider" />

        <div className="taskbar-windows">
          {visibleWindows.map((window) => (
            <button
              key={window.id}
              className={`taskbar-window-button ${
                window.id === activeWindowId && !window.minimized ? 'active' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleWindowClick(window.id, window.minimized);
              }}
            >
              {window.icon && <span style={{ marginRight: '4px' }}>{window.icon}</span>}
              {window.title}
            </button>
          ))}
        </div>

        <div className="taskbar-tray">
          <span className="system-tray-icon" title="AI_Process.exe">
            🤖
          </span>
          <div className="taskbar-clock">{formatTime(time)}</div>
        </div>
      </div>
    </>
  );
};

export default Taskbar;
